import type { WidenMediaElement } from '@hub/libs/widen-utils';
import { MediaElementType } from '@hub/libs/widen-utils';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'next-i18next';
import { useKeenSlider } from 'keen-slider/react';
import { useIsomorphicLayoutEffect } from '@hub/hooks/useIsomorphicLayoutEffect';
import classNames from 'classnames';
import styles from './ProductMediaCarousel.module.css';
import { WidenImage } from '@hub/common/Widen/WidenImage';

export interface ProductMediaFadeCarouselProps {
  activeIndex: number;
  media: WidenMediaElement[];
  onChange: (idx: number) => void;
  onClick: (idx: number) => void;
}

export const ProductMediaFadeCarousel = ({ activeIndex, media, onChange, onClick }: ProductMediaFadeCarouselProps) => {
  const [dragging, setDragging] = useState(false);
  const isMountedRef = useRef<boolean>(false);
  const { t } = useTranslation('product/productMediaCarousel');
  const [carouselRef, carousel] = useKeenSlider<HTMLDivElement>({
    initial: activeIndex,
    spacing: 0,
    loop: true,
    slides: media.length,
    slidesPerView: 1,
    slideChanged: s => {
      if (
        !isMountedRef.current ||
        Number.isNaN(s.details().relativeSlide) ||
        s.details().relativeSlide === activeIndex
      ) {
        return;
      }
      onChange(s.details().relativeSlide);
    },
    dragStart: () => {
      /* istanbul ignore if */
      if (!isMountedRef.current || !dragging) {
        return;
      }
      setDragging(true);
    },
    dragEnd: s => {
      /* istanbul ignore if */
      if (isMountedRef.current && dragging && Math.abs(s.details().speed) < 1) {
        setDragging(false);
      }
    },
    afterChange: () => {
      if (!isMountedRef.current || !dragging) {
        return;
      }
      setDragging(false);
    },
  });

  useEffect(() => {
    if (carousel) {
      carousel.resize();
    }
  }, [carousel]);

  useEffect(() => {
    if (carousel && !Number.isNaN(activeIndex) && carousel.details().relativeSlide !== activeIndex) {
      carousel.moveToSlideRelative(activeIndex, true);
    }
  }, [activeIndex, carousel]);

  useEffect(() => {
    if (carousel) {
      carousel.refresh();
    }
  }, [media, carousel]);

  useIsomorphicLayoutEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  return (
    <>
      {/* when in full-width mode, set width to 100%. height cannot be a percentage value, therefore
       * set it to zero and instead use a paddingBottom of 67% (of the width) to keep the images aspect ratio correct */}
      <div
        data-testid="product-image-carousel-fade"
        ref={carouselRef}
        className={classNames('relative', 'max-w-full', 'max-h-full', styles.fadeCarousel)}
        style={{
          height: 0,
          minHeight: 0,
          paddingBottom: '67%',
        }}
      >
        {media.map((img, idx) => (
          <div
            className={classNames(
              'cursor-pointer relative transition-opacity duration-500',
              styles.fadeCarousel__element,
              activeIndex === idx ? 'opacity-100' : 'opacity-0'
            )}
            key={`fade-carousel-image-${img.shortCode}`}
            onClick={() => !dragging && onClick(idx)}
          >
            <WidenImage
              width={360}
              height={240}
              alt={img.description || img.title}
              shortCode={img.shortCode}
              filename={img.filename}
              layout="responsive"
              isPreviewImage
              isVideo={img.type === MediaElementType.VIDEO}
              title={img.title}
              priority={img.priority}
            />
          </div>
        ))}
      </div>
      <div className="text-center text-xs my-3">
        {t('PRODUCT_MEDIA_CAROUSEL.CURRENT_INDEX', {
          active: activeIndex + 1,
          total: media.length,
          ns: 'bearing-hub/bearingHub',
        })}
      </div>
    </>
  );
};
